import { listDriveFiles, deleteDriveFile, updateDriveFile, uploadDriveFile } from '@/services/api/drive'

export default {
  namespaced: true,
  state: {
    files: [],
    loading: false,
    storage: { used: 0, total: 500 * 1024 * 1024 },
    uploading: [], // [{ id, name, progress, status }]
  },
  getters: {
    allFiles: (state) => state.files.filter((f) => !f.deletedAt),
    recentFiles: (state) =>
      state.files
        .filter((f) => !f.deletedAt)
        .slice()
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .slice(0, 10),
    starredFiles: (state) => state.files.filter((f) => !f.deletedAt && f.starred),
    trashedFiles: (state) => state.files.filter((f) => !!f.deletedAt),
    filesByTag: (state) => (tag) =>
      state.files.filter((f) => !f.deletedAt && f.tags && f.tags.includes(tag)),
    allTags: (state) => {
      const tagSet = new Set()
      state.files.forEach((f) => (f.tags || []).forEach((t) => tagSet.add(t)))
      return [...tagSet]
    },
    storagePercent: (state) =>
      state.storage.total > 0
        ? Math.round((state.storage.used / state.storage.total) * 100)
        : 0,
  },
  mutations: {
    SET_FILES(state, files) {
      state.files = files
    },
    SET_LOADING(state, val) {
      state.loading = val
    },
    SET_STORAGE(state, storage) {
      state.storage = storage
    },
    UPSERT_FILE(state, file) {
      const idx = state.files.findIndex((f) => f.id === file.id)
      if (idx >= 0) {
        state.files.splice(idx, 1, file)
      } else {
        state.files.unshift(file)
      }
    },
    REMOVE_FILE(state, fileId) {
      state.files = state.files.filter((f) => f.id !== fileId)
    },
    ADD_UPLOAD(state, upload) {
      state.uploading.push(upload)
    },
    UPDATE_UPLOAD(state, { id, ...patch }) {
      const idx = state.uploading.findIndex((u) => u.id === id)
      if (idx >= 0) Object.assign(state.uploading[idx], patch)
    },
    REMOVE_UPLOAD(state, id) {
      state.uploading = state.uploading.filter((u) => u.id !== id)
    },
  },
  actions: {
    async fetchFiles({ commit }) {
      commit('SET_LOADING', true)
      try {
        const res = await listDriveFiles()
        commit('SET_FILES', res.data.files || [])
        if (res.data.storage) commit('SET_STORAGE', res.data.storage)
      } catch (err) {
        console.error(err)
        commit(
          'notification/NEW',
          { type: 'error', message: 'Drive Error', description: 'Failed to load files' },
          { root: true },
        )
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async uploadFile({ commit }, file) {
      const tempId = `upload-${Date.now()}-${Math.random()}`
      commit('ADD_UPLOAD', { id: tempId, name: file.name, progress: 0, status: 'uploading' })
      try {
        const driveFile = await uploadDriveFile(file, (progress) => {
          commit('UPDATE_UPLOAD', { id: tempId, progress })
        })
        commit('UPSERT_FILE', driveFile)
        commit('REMOVE_UPLOAD', tempId)
        commit(
          'notification/NEW',
          { type: 'success', message: 'Uploaded', description: `${file.name} is ready` },
          { root: true },
        )
        return driveFile
      } catch (err) {
        console.error(err)
        commit('UPDATE_UPLOAD', { id: tempId, status: 'failed', progress: 0 })
        commit(
          'notification/NEW',
          { type: 'error', message: 'Upload Failed', description: `Could not upload ${file.name}` },
          { root: true },
        )
      }
    },

    async deleteFile({ commit }, fileId) {
      try {
        await deleteDriveFile(fileId)
        commit('REMOVE_FILE', fileId)
        commit(
          'notification/NEW',
          { type: 'success', message: 'Deleted', description: 'File removed' },
          { root: true },
        )
      } catch (err) {
        console.error(err)
        commit(
          'notification/NEW',
          { type: 'error', message: 'Error', description: 'Failed to delete file' },
          { root: true },
        )
      }
    },

    async updateFile({ commit }, { fileId, data }) {
      try {
        const res = await updateDriveFile(fileId, data)
        commit('UPSERT_FILE', res.data)
        return res.data
      } catch (err) {
        console.error(err)
        commit(
          'notification/NEW',
          { type: 'error', message: 'Error', description: 'Failed to update file' },
          { root: true },
        )
      }
    },
  },
}
